import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Send, UserPlus, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { API, authHeaders, fmtDate } from "./adminApi";

const TeamSection = () => {
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [adding, setAdding] = useState(false);

  const fetchTeam = () => {
    fetch(`${API}/api/admin/team`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setTeam)
      .catch(() => setTeam([]));
  };

  useEffect(fetchTeam, []);

  const addMember = async () => {
    if (!/\S+@\S+\.\S+/.test(form.email) || form.password.length < 8) {
      toast.error("Enter a valid email and a password of at least 8 characters");
      return;
    }
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/admin/team`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Could not add team member");
        return;
      }
      toast.success(`${form.email} added as ${form.role} — share their login details with them`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      fetchTeam();
    } catch {
      toast.error("Could not add team member");
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (member) => {
    const res = await fetch(`${API}/api/admin/team/${encodeURIComponent(member.email)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ active: !member.active }),
    });
    if (res.ok) {
      toast.success(member.active ? "Access suspended" : "Access restored");
      fetchTeam();
    } else {
      toast.error("Could not update team member");
    }
  };

  const resetPassword = async (member) => {
    const pw = window.prompt(`New password for ${member.email} (min 8 characters):`);
    if (!pw) return;
    const res = await fetch(`${API}/api/admin/team/${encodeURIComponent(member.email)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ password: pw }),
    });
    const data = await res.json();
    if (res.ok) toast.success("Password updated");
    else toast.error(data.detail || "Could not update password");
  };

  const removeMember = async (member) => {
    if (!window.confirm(`Remove ${member.email}? They will lose admin access.`)) return;
    const res = await fetch(`${API}/api/admin/team/${encodeURIComponent(member.email)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (res.ok) {
      toast.success("Team member removed");
      fetchTeam();
    } else {
      toast.error("Could not remove team member");
    }
  };

  if (team === null) return <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-primary" size={22} /></div>;

  return (
    <div data-testid="team-section" className="border-t border-border/60 pt-6">
      <h3 className="font-heading text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
        <UserPlus size={17} className="text-primary" /> Team Accounts
      </h3>
      <p className="font-body text-sm text-muted-foreground mb-4">
        Staff can manage bookings, services, specials, and offers. Admins get full access — including Settings and team management. Only the owner account is untouchable.
      </p>
      <div className="space-y-2 mb-5">
        {team.map((m) => (
          <div key={m.email} data-testid={`team-member-${m.email}`} className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 ${m.active !== false ? "border-border/60 bg-white" : "border-border/40 bg-muted/40 opacity-70"}`}>
            <div className="flex-1 min-w-[160px]">
              <p className="font-body text-sm text-foreground">{m.name || "—"} <span className="text-muted-foreground">· {m.email}</span></p>
              <p className="font-body text-xs text-muted-foreground">Added {fmtDate(m.created_at)}</p>
            </div>
            <span className={`font-body text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${m.role === "owner" ? "bg-primary/10 text-primary" : m.role === "admin" ? "bg-accent/10 text-accent" : "bg-muted text-muted-foreground"}`}>
              {m.role === "owner" ? "Owner" : m.role === "admin" ? "Admin" : "Staff"}
            </span>
            {m.role !== "owner" && (
              <>
                <Switch data-testid={`team-toggle-${m.email}`} checked={m.active !== false} onCheckedChange={() => toggleActive(m)} />
                <button data-testid={`team-reset-${m.email}`} onClick={() => resetPassword(m)} className="text-muted-foreground hover:text-primary p-1" aria-label="Reset password" title="Reset password">
                  <KeyRound size={15} />
                </button>
                <button data-testid={`team-remove-${m.email}`} onClick={() => removeMember(m)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Remove">
                  <Trash2 size={15} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <Input data-testid="team-new-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="font-body" />
        <Input data-testid="team-new-email" type="email" placeholder="staff@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="font-body" />
        <Input data-testid="team-new-password" type="text" placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="font-body" />
        <select data-testid="team-new-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-9 rounded-md border border-input bg-white px-3 font-body text-sm">
          <option value="staff">Staff</option>
          <option value="admin">Admin (full access)</option>
        </select>
      </div>
      <Button data-testid="team-add-button" onClick={addMember} disabled={adding} className="mt-3 bg-primary hover:bg-primary/90 text-white font-body rounded-full">
        {adding ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} className="mr-1" /> Add Account</>}
      </Button>
    </div>
  );
};

export const SettingsTab = () => {
  const [emails, setEmails] = useState(null);
  const [sender, setSender] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/settings`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setEmails(d.alert_emails);
        setSender(d.sender_email);
      })
      .catch(() => setEmails([]));
  }, []);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!/\S+@\S+\.\S+/.test(e)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (emails.includes(e)) {
      toast.error("That email is already in the list");
      return;
    }
    setEmails([...emails, e]);
    setNewEmail("");
  };

  const save = async () => {
    if (!emails.length) {
      toast.error("Keep at least one alert email");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ alert_emails: emails }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.detail || "Could not save settings");
        return;
      }
      toast.success("Alert emails saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/admin/settings/test-email`, { method: "POST", headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Test email failed");
        return;
      }
      toast.success(`Test email sent to ${data.to.join(", ")}`);
    } catch {
      toast.error("Test email failed");
    } finally {
      setTesting(false);
    }
  };

  if (emails === null) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div data-testid="admin-settings-tab" className="max-w-xl space-y-6">
      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground mb-1">Booking Alert Emails</h3>
        <p className="font-body text-sm text-muted-foreground mb-4">
          Every new paid booking sends an instant alert to these addresses. Guests always receive their confirmation at the email they book with.
        </p>
        <div className="space-y-2 mb-4">
          {emails.map((e) => (
            <div key={e} data-testid={`alert-email-${e}`} className="flex items-center justify-between rounded-lg border border-border/60 bg-white px-4 py-2.5 font-body text-sm">
              <span className="text-foreground">{e}</span>
              <button data-testid={`remove-alert-email-${e}`} onClick={() => setEmails(emails.filter((x) => x !== e))} className="text-muted-foreground hover:text-destructive p-1" aria-label="Remove">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="new-alert-email"
            type="email"
            placeholder="add-another@email.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addEmail()}
            className="font-body"
          />
          <Button data-testid="add-alert-email" variant="outline" onClick={addEmail} className="font-body rounded-full shrink-0">
            <Plus size={15} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="flex gap-3">
        <Button data-testid="save-settings" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
          {saving ? <Loader2 size={16} className="animate-spin" /> : "Save"}
        </Button>
        <Button data-testid="send-test-email" variant="outline" onClick={sendTest} disabled={testing} className="font-body rounded-full">
          {testing ? <Loader2 size={16} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Send Test Email
        </Button>
      </div>

      <p className="font-body text-xs text-muted-foreground border-t border-border/60 pt-4">
        Emails are sent from <span className="font-medium text-foreground">{sender || "(sender not configured)"}</span> via SendGrid.
        This address must be verified in SendGrid → Settings → Sender Authentication, or emails will not deliver.
      </p>

      <TeamSection />
    </div>
  );
};
